import {
  GetDeviceEventsResponse,
  GetDeviceEventsBody,
  DeviceEventHit,
} from '@eppendorf/vnls-device-service-utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetcher } from '$shared/fetch';
import {
  DEVICE_EVENTS_V2_BASE_PATH,
  objectToURLSearchParams,
} from '$shared/utils/api-paths';

export const deviceEventsV2QueryKey = 'deviceEventsV2';

function shouldPollForNewEvents(endDate?: string | null) {
  if (!endDate) {
    return true;
  }

  const timestamp = Number.parseInt(endDate, 10);

  if (!Number.isFinite(timestamp)) {
    return true;
  }

  const endDateTime = new Date(timestamp);

  const endDateIsInThePast =
    endDateTime.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);

  if (endDateIsInThePast) {
    return false;
  }

  return true;
}

export const createDeviceEventsQueryWithQueryParams = (
  queryParams: GetDeviceEventsBody,
) => ({
  queryKey: [deviceEventsV2QueryKey, ...objectToURLSearchParams(queryParams).values()],
  queryFn: async () => {
    const result = await fetcher.post<GetDeviceEventsBody, GetDeviceEventsResponse>(
      DEVICE_EVENTS_V2_BASE_PATH,
      queryParams,
    );

    return result.data.items;
  },
  staleTime: 5000,
  placeholderData: keepPreviousData,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
});

export const getNewEvents = (queryParams: GetDeviceEventsBody) => ({
  queryKey: ['latestDeviceEventsV2', ...objectToURLSearchParams(queryParams).values()],
  queryFn: async () => {
    const result = await fetcher.post<GetDeviceEventsBody, GetDeviceEventsResponse>(
      DEVICE_EVENTS_V2_BASE_PATH,
      queryParams,
    );

    return result.data.items;
  },
  staleTime: 1000,
  placeholderData: keepPreviousData,
  refetchInterval: () => {
    const shouldPoll = shouldPollForNewEvents(queryParams.to);
    return shouldPoll ? 5000 : false;
  },
});

export const useDeviceEventsWithQueryParamsV2 = (queryParams: GetDeviceEventsBody) =>
  useQuery(createDeviceEventsQueryWithQueryParams(queryParams));

export const useLatestDeviceEventsWithQueryParamsV2 = (
  queryParams: GetDeviceEventsBody,
) => useQuery<DeviceEventHit[]>(getNewEvents({ ...queryParams, page: 1 }));
